/**
 * <ConsolidationSuggestions country />  - FCs with enough open-PO demand to plan a shipment
 * (GET /api/ai/consolidation-recommendations). Shown above the FC Demand tab of FC Shipments; nothing when empty.
 * Moved here from the old AI page.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Clock, Warehouse } from 'lucide-react';
import { getAuthHeaders } from '../contexts/AuthContext';
import { LoadError } from './LoadError';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const PRIORITY = { HIGH: 'bg-red-100 text-red-700', MEDIUM: 'bg-amber-100 text-amber-700', LOW: 'bg-gray-100 text-gray-700' };

export const ConsolidationSuggestions = ({ country }) => {
  const [state, setState] = useState({ rows: [], error: null, loading: true });
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    axios.get(`${API}/ai/consolidation-recommendations`, { params: { country }, headers: getAuthHeaders(), signal: controller.signal })
      .then(res => setState({ rows: Array.isArray(res.data) ? res.data : [], error: null, loading: false }))
      .catch(error => { if (!controller.signal.aborted) setState({ rows: [], error, loading: false }); });
    return () => controller.abort();
  }, [country, version]);
  if (state.error) return <LoadError what="consolidation suggestions" error={state.error} onRetry={() => setVersion(v => v + 1)} retrying={state.loading} testId="consolidation-suggestions-error" />;
  if (!state.rows.length) return null;
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4" aria-labelledby="consolidation-title" data-testid="consolidation-suggestions">
      <h2 id="consolidation-title" className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <Warehouse className="h-4 w-4 text-green-600" aria-hidden="true" /> Ready to consolidate
      </h2>
      <p className="mt-0.5 text-xs text-gray-500">FCs with enough open PO units for a shipment. Plan one from the FC Demand table below.</p>
      <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {state.rows.slice(0, 6).map(rec => (
          <li key={`${rec.fc_code}-${rec.city || ''}`} className="rounded-lg border border-gray-200 p-3 text-xs">
            <div className="flex items-start justify-between gap-2">
              <span><span className="text-sm font-semibold text-gray-900">{rec.fc_code}</span>{rec.city && <span className="ml-1 text-gray-500">{rec.city}</span>}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${PRIORITY[rec.priority] || PRIORITY.LOW}`}>{rec.priority}</span>
            </div>
            <dl className="mt-2 grid grid-cols-3 gap-1 text-gray-600">
              <div><dt>Open POs</dt><dd className="font-semibold tabular-nums text-gray-900">{rec.po_count}</dd></div>
              <div><dt>Units</dt><dd className="font-semibold tabular-nums text-gray-900">{Number(rec.remaining_units || 0).toLocaleString('en-IN')}</dd></div>
              <div><dt>Containers</dt><dd className="font-semibold tabular-nums text-gray-900">{rec.estimated_containers}</dd></div>
            </dl>
            {rec.urgent_pos > 0 && <p className="mt-1.5 inline-flex items-center gap-1 font-semibold text-red-700"><Clock className="h-3 w-3" aria-hidden="true" />{rec.urgent_pos} urgent PO{rec.urgent_pos > 1 ? 's' : ''}</p>}
            {rec.recommended_action && <p className="mt-1.5 text-gray-600">{rec.recommended_action}</p>}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default ConsolidationSuggestions;
